import { useMutation, useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';

import { api } from '../lib/api.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import { Icon } from './Icons.js';
import { InboxCard } from './InboxCard.js';
import { HealthPanel } from './HealthPanel.js';

/**
 * The first screen after signing in.
 *
 * One block of figures, then where to go. Not two blocks: numbers split across
 * separate cards read as separate subjects, and the taller the block the further
 * the destinations fall below the fold on a phone — which is the whole reason
 * they are there.
 *
 * The figures come from Meta, not from our own records. See
 * apps/api/src/services/insights.ts for why counting what this system
 * published would be the wrong number.
 */

const WINDOW_DAYS = 7;

interface Day {
  label: string;
  count: number;
}

function lastSevenDays(times: string[], locale: string): Day[] {
  const days: Day[] = [];
  const now = new Date();

  for (let back = WINDOW_DAYS - 1; back >= 0; back -= 1) {
    const day = new Date(now);
    day.setDate(now.getDate() - back);
    day.setHours(0, 0, 0, 0);
    const next = new Date(day);
    next.setDate(day.getDate() + 1);

    const count = times.filter((time) => {
      const at = new Date(time).getTime();
      return at >= day.getTime() && at < next.getTime();
    }).length;

    days.push({
      label: day.toLocaleDateString(locale, { weekday: 'short' }),
      count,
    });
  }

  return days;
}

export function HomePanel({
  onGo,
  canOpen,
}: {
  onGo: (tab: string) => void;
  /**
   * Whether this person can open a panel at all.
   *
   * Passed down rather than worked out here, because the navigation above
   * already decides it and two answers to "may I open this" is one too many.
   *
   * Without it this screen offered an operator five shortcuts to panels their
   * own tab row does not show. Pressing one sent them straight back here — no
   * error, no explanation, nothing happening — which is worse than a refusal
   * they can read.
   */
  canOpen: (tab: string) => boolean;
}) {
  const { t, language } = useI18n();
  const locale = language === 'ar' ? 'ar' : 'en';

  /**
   * The first of these a person can open, or nothing.
   *
   * Sections do not all begin with the panel everybody holds — "Pages" starts
   * at Connections, which is an administrator's screen — so a shortcut names
   * the panels it would settle for, in order of preference.
   */
  const firstOpen = (...tabs: string[]): string | null => tabs.find((tab) => canOpen(tab)) ?? null;

  const reconnect = useMutation({
    mutationFn: api.startOAuth,
    // Full navigation, as on the Connections screen: Meta's dialog is a page.
    onSuccess: (result) => {
      window.location.href = result.authorizationUrl;
    },
  });

  const overview = useQuery({
    queryKey: ['overview'],
    queryFn: () => api.overview(),
    // The waiting count is the reason this screen exists; it should move when
    // the work does, without a reload.
    refetchInterval: 60_000,
  });

  // The few newest items, shown in full. Enough to answer one or two without
  // going anywhere, and short enough that it stays a summary.
  const inbox = useQuery({
    queryKey: ['inbox', '', 'waiting'],
    queryFn: () => api.inbox({ status: 'waiting' }),
  });

  const data = overview.data;
  const days = lastSevenDays(data?.recentPostTimes ?? [], locale);
  /*
   * The week's total is the bars' sum, not a second count: one was Meta's last
   * 168 hours and the other our own published list by calendar day, and they
   * disagreed (35 against 27) with nothing to say why. Unknown stays unknown.
   */
  const weekTotal =
    data?.postsLast7Days === null || data === undefined
      ? data?.postsLast7Days
      : days.reduce((sum, day) => sum + day.count, 0);
  const busiest = Math.max(1, ...days.map((day) => day.count));

  /*
   * Nothing connected yet is a different screen, not an empty one. A grid of
   * zeros tells a new operator the product is broken; one instruction tells
   * them what to do.
   */
  if (overview.isFetched && data?.activePages === 0) {
    return (
      <section className="panel home-start">
        <span className="home-start-mark">
          <Icon.link />
        </span>
        <h2>{t('home.startTitle')}</h2>
        {/* Telling somebody to connect a Page they have no way to connect is
            worse than telling them nothing. They get the fact and who to ask. */}
        <p className="muted">
          {canOpen('connections') ? t('home.startBody') : t('home.startBodyNoAccess')}
        </p>
        {canOpen('connections') && (
          <button type="button" className="primary" onClick={() => onGo('connections')}>
            <Icon.link />
            {t('home.startAction')}
          </button>
        )}
      </section>
    );
  }

  return (
    <div className="home">
      {/* Below the waiting work but above the figures. What is owed comes
          first; what is broken comes before what is merely interesting. */}
      {/* Above the figures on purpose. Numbers are knowledge; this is work —
          and a person opens this screen to find out what is owed, not how the
          week went. */}
      {(data?.waitingReplies ?? 0) > 0 && (
        <section className="panel waiting">
          <div className="panel-head">
            <h2>
              {t('home.waitingTitle')}
              <span className="inbox-count">{data?.waitingReplies}</span>
            </h2>
            <button
              type="button"
              className="ghost small"
              onClick={() => onGo(firstOpen('conversations', 'inbox') ?? 'home')}
            >
              {t('home.openInbox')}
            </button>
          </div>

          <ul className="inbox-list">
            {(inbox.data?.items ?? []).slice(0, 3).map((item) => (
              <InboxCard key={item.id} item={item} locale={locale} readOnly={false} />
            ))}
          </ul>
        </section>
      )}

      <HealthPanel onGo={onGo} />

      <section className="panel stats">
        <div className="panel-head">
          <h2>{t('home.greeting')}</h2>
          <button type="button" className="ghost small" onClick={() => onGo('insights')}>
            {t('home.viewAll')}
          </button>
        </div>
        <p className="muted small stats-note">{t('home.fromMeta')}</p>

        {/* Laid out along the row rather than stacked, which is what keeps this
            block short enough that the destinations stay on screen. */}
        {/* Each figure is also a way in, so a figure whose panel is closed to
            this person is dropped rather than shown as a dead button. */}
        <div className="tiles">
          {canOpen('pages') && (
            <Tile
              icon={<Icon.pages />}
              tone="pages"
              value={data?.activePages}
              loading={overview.isLoading}
              label={t('home.livePages')}
              onClick={() => onGo('pages')}
            />
          )}
          {canOpen('published') && (
            <Tile
              icon={<Icon.check />}
              tone="publish"
              value={weekTotal}
              loading={overview.isLoading}
              label={t('home.publishedThisWeek')}
              onClick={() => onGo('published')}
            />
          )}
          {canOpen('comments') && (
            <Tile
              icon={<Icon.comment />}
              tone="engage"
              value={data?.comments}
              loading={overview.isLoading}
              label={t('home.comments')}
              onClick={() => onGo('comments')}
            />
          )}
          {canOpen('publish') && (
            <Tile
              icon={<Icon.list />}
              tone="jobs"
              value={data?.queued}
              loading={overview.isLoading}
              label={t('home.running')}
              onClick={() => onGo('publish')}
            />
          )}
          {canOpen('conversations') && (
            <Tile
              icon={<Icon.comment />}
              tone="engage"
              value={data?.waitingReplies}
              loading={overview.isLoading}
              label={t('home.waitingLabel')}
              onClick={() => onGo('conversations')}
            />
          )}
        </div>

        {/* One series, so no legend — the heading above names it. Bars because
            seven days are seven discrete buckets, not a continuum. */}
        <div className="spark" role="img" aria-label={t('home.activityTitle')}>
          {days.map((day, index) => (
            <div className="spark-col" key={index}>
              <div className="spark-track">
                <div
                  className={day.count === 0 ? 'spark-bar empty' : 'spark-bar'}
                  style={{
                    height: `${Math.round((day.count / busiest) * 100)}%`,
                  }}
                >
                  <span
                    className={
                      day.count === busiest && day.count > 0 ? 'spark-value shown' : 'spark-value'
                    }
                  >
                    {day.count}
                  </span>
                </div>
              </div>
              <span className="spark-label">{day.label}</span>
            </div>
          ))}
        </div>

        {/* A low figure with an explanation beats a low figure without one, and
            an explanation that says what to press beats both. */}
        {(data?.revokedPages?.length ?? 0) > 0 && (
          <div className="notice warn revoked-card">
            <div>
              <strong>{t('home.revokedTitle', { count: data?.revokedPages?.length ?? 0 })}</strong>
              <p className="small" style={{ margin: '4px 0 0' }}>
                {t('home.revokedBody', {
                  names:
                    (data?.revokedPages ?? [])
                      .slice(0, 5)
                      .map((page) => page.name)
                      .join('، ') +
                    ((data?.revokedPages?.length ?? 0) > 5
                      ? ` +${(data?.revokedPages?.length ?? 0) - 5}`
                      : ''),
                })}
              </p>
            </div>
            {canOpen('connections') && (
              <button
                type="button"
                className="primary small"
                onClick={() => reconnect.mutate()}
                disabled={reconnect.isPending}
              >
                {t('home.revokedAction')}
              </button>
            )}
          </div>
        )}
      </section>

      {/* One per section, opening the first panel in it this person can reach.
          A section they can reach nothing in is not shown at all. */}
      <div className="destinations">
        {[
          {
            tone: 'pages',
            icon: <Icon.pages />,
            label: t('nav.pages'),
            tab: firstOpen('pages', 'page-profile'),
          },
          {
            tone: 'publish',
            icon: <Icon.send />,
            label: t('nav.publish'),
            tab: firstOpen('publish', 'published'),
          },
          {
            tone: 'engage',
            icon: <Icon.comment />,
            label: t('nav.engage'),
            tab: firstOpen('conversations', 'inbox', 'comments'),
          },
          {
            tone: 'activity',
            icon: <Icon.logs />,
            label: t('nav.activity'),
            tab: firstOpen('insights', 'logs'),
          },
          {
            tone: 'account',
            icon: <Icon.user />,
            label: t('nav.settings'),
            // The same order the Settings section uses, so the shortcut lands
            // where pressing the section itself would.
            tab: firstOpen('account', 'connections', 'whatsapp', 'automation'),
          },
        ]
          .filter((entry) => entry.tab !== null)
          .map((entry) => (
            <Destination
              key={entry.tone}
              icon={entry.icon}
              tone={entry.tone}
              label={entry.label}
              onClick={() => onGo(entry.tab as string)}
            />
          ))}
      </div>
    </div>
  );
}

function Tile({
  icon,
  tone,
  value,
  loading,
  label,
  onClick,
}: {
  icon: ReactElement;
  tone: string;
  value: number | null | undefined;
  /** The first answer has not arrived: a shimmer, not a dash that reads as "none". */
  loading?: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button type="button" className="tile" data-tone={tone} onClick={onClick}>
      <span className="tile-icon">{icon}</span>
      <span className="tile-body">
        {/* A dash while the figure is still being fetched. A zero shown before
            the answer arrives is a wrong answer, not a placeholder. */}
        {loading ? (
          <span className="tile-skeleton" aria-busy="true" />
        ) : (
          <strong className="tile-value">{value ?? '—'}</strong>
        )}
        <span className="tile-label">{label}</span>
      </span>
    </button>
  );
}

function Destination({
  icon,
  tone,
  label,
  onClick,
}: {
  icon: ReactElement;
  tone: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button type="button" className="destination" data-tone={tone} onClick={onClick}>
      <span className="destination-icon">{icon}</span>
      <span>{label}</span>
    </button>
  );
}
